import * as ToastPrimitive from '@radix-ui/react-toast';
import { Check, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { interactive } from '@/components/ui/interactive';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

/**
 * The confirmation toast from the prototype — navy pill, gold check, bottom corner.
 *
 * The prototype's `notify()` replaced a single `toast` object and cleared it on a
 * `setTimeout`, so a second action fired inside 2.6 seconds silently swallowed the first
 * message. The audit queue is exactly where that happens: an admin approves four
 * documents in a row and sees one toast. Here every call pushes its own entry and Radix
 * stacks them.
 *
 * Position is `end-6` rather than the prototype's `left-6`: bottom-left in Arabic,
 * bottom-right in English, one class.
 */
export interface ToastMessage {
  id: number;
  title: string;
  description?: string;
  tone?: 'default' | 'danger';
}

interface ToastContextValue {
  toast: (message: Omit<ToastMessage, 'id'>) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast must be used inside <Toaster>');
  return value;
}

export function Toaster({
  children,
  closeLabel,
  duration = 2600,
}: {
  children: ReactNode;
  /** Accessible name for a toast's dismiss control — always a translated string. */
  closeLabel: string;
  duration?: number;
}) {
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  // A ref, not state: the id only has to be unique, and bumping state here would make
  // `toast()` a two-render operation that StrictMode double-invokes into duplicates.
  const nextId = useRef(1);

  const toast = useCallback((message: Omit<ToastMessage, 'id'>) => {
    const id = nextId.current++;
    setMessages((current) => [...current, { ...message, id }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setMessages((current) => current.filter((message) => message.id !== id));
  }, []);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider duration={duration} swipeDirection="down">
        {children}

        {messages.map((message) => (
          <ToastPrimitive.Root
            key={message.id}
            data-slot="toast"
            onOpenChange={(open) => {
              if (!open) dismiss(message.id);
            }}
            className={cn(
              'flex items-start gap-2.5 rounded-xl px-4 py-3 text-[13px] font-semibold text-white shadow-xl',
              'data-[state=closed]:animate-toast-out data-[state=open]:animate-toast-in',
              message.tone === 'danger' ? 'bg-rose-600' : 'bg-navy',
            )}
          >
            {message.tone === 'danger' ? (
              <X aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <Check aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
            )}
            <div className="min-w-0 flex-1">
              <ToastPrimitive.Title>{message.title}</ToastPrimitive.Title>
              {message.description && (
                <ToastPrimitive.Description className="mt-0.5 text-[12px] font-normal opacity-80">
                  {message.description}
                </ToastPrimitive.Description>
              )}
            </div>
            <ToastPrimitive.Close
              aria-label={closeLabel}
              className={cn(interactive, 'shrink-0 rounded p-0.5 opacity-60 hover:opacity-100')}
            >
              <X className="h-3.5 w-3.5" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}

        <ToastPrimitive.Viewport className="fixed end-6 bottom-6 z-[70] flex w-80 max-w-[calc(100vw-3rem)] flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}
